import { Account } from 'model/IChat';
import React from 'react';
import BookBlockButton from 'reusable-components/book-block/BookBlockButton';
import BookBlockDateSelector from 'reusable-components/book-block/BookBlockDateSelector';
import BookBlockLocals from 'reusable-components/book-block/BookBlockLocals';
import BookBlockPeopleSelector from 'reusable-components/book-block/BookBlockPeopleSelector';
import BookBlockPrice from 'reusable-components/book-block/BookBlockPrice';
import BookBlockRating from 'reusable-components/book-block/BookBlockRating';
import BookBlockSelectedLocal from 'reusable-components/book-block/BookBlockSelectedLocal';
import BookBlockTimeSelector from 'reusable-components/book-block/BookBlockTimeSelector';
import { useTrekker } from 'utils/analytics';

const CSS = require('./BookBlock.module.scss');

interface BookBlockProps {
  details: {
    rating: number;
    reviews: number;
    locals: Partial<Account>[];
  };
  config: {
    buttonText: React.ReactNode;
    maxPeople: number;
    kidsForFree?: boolean;
    isOriginals?: boolean;
    isLocalPicker?: boolean;
    preferredLocal?: Partial<Account>;
    nrOfLocals: number;
  };
  values: {
    selectedDate?: string;
    availableDates: { date: string; locals: string[] }[];
    selectedTime?: string;
    availableTimes: { time: string; locals: string[] }[];
    nrOfAdults: number;
    nrOfChildren: number;
    formattedPrice: string;
  };
  actions: {
    onDateChange: (date: string) => void;
    onTimeChange: (time: string) => void;
    onAdultsChange: (adults: number) => void;
    onChildrenChange: (children: number) => void;
    onContactHost: () => void;
    onReviews: () => void;
    onBookButton: () => void;
  };
  loading: {
    availableDates: boolean;
    availableTimes: boolean;
  };
}

function BookBlock({
  details,
  config,
  values,
  actions,
  loading,
}: BookBlockProps) {
  const dateSelectRef = React.useRef(null);
  const timeSelectRef = React.useRef(null);
  const trekker = useTrekker();

  const hasAvailableSelectedDate =
    values.availableDates &&
    values.availableDates?.some(
      ({ date, locals }) =>
        values.selectedDate === date &&
        locals?.includes(config.preferredLocal?.id),
    );

  const hasAvailableSelectedTime =
    values.availableTimes &&
    values.availableTimes?.some(
      ({ time, locals }) =>
        time === values.selectedTime &&
        locals.includes(config.preferredLocal?.id),
    );

  const onBookButton = () => {
    const shouldOpenDatePicker =
      !values.selectedDate ||
      (config.isLocalPicker && !hasAvailableSelectedDate);

    if (shouldOpenDatePicker) {
      return dateSelectRef.current?.click();
    }

    const shouldOpenTimePicker =
      !values.selectedTime ||
      (config.isLocalPicker && !hasAvailableSelectedTime);

    if (shouldOpenTimePicker) {
      return timeSelectRef.current?.click();
    }

    trekker?.event('click', {
      component: ['book-button'],
      buttonText: String(config.buttonText),
      nrOfAdults: values.nrOfAdults,
      nrOfChildren: values.nrOfChildren,
      date: values.selectedDate,
      time: values.selectedTime,
      host: config.preferredLocal,
    });
    return actions.onBookButton();
  };

  const onDateChange = (date: string) => {
    dateSelectRef.current?.click();
    trekker?.event('click', {
      component: ['day-selector'],
      date,
      nrOfAdults: values.nrOfAdults,
      nrOfChildren: values.nrOfChildren,
      host: config.preferredLocal,
      buttonText: 'Select date',
    });
    return actions.onDateChange(date);
  };

  const onTimeChange = (time: string) => {
    timeSelectRef.current?.click();
    trekker?.event('click', {
      component: ['time-selector'],
      date: values.selectedDate,
      time: time,
      nrOfAdults: values.nrOfAdults,
      nrOfChildren: values.nrOfChildren,
      host: config.preferredLocal,
      buttonText: 'Start time',
    });
    return actions.onTimeChange(time);
  };

  return (
    <section className={CSS.BookBlock}>
      <div className={CSS.BookBlockHeader}>
        <BookBlockPrice
          formattedPrice={values.formattedPrice}
          kidsForFree={config.kidsForFree}
        />
        <BookBlockRating
          rating={details.rating}
          nrOfReviews={details.reviews}
          onReviews={actions.onReviews}
        />
      </div>
      <div className={CSS.BookBlockControls}>
        <BookBlockDateSelector
          ref={dateSelectRef}
          selectedDate={values.selectedDate}
          onDateChange={onDateChange}
          availableDates={values.availableDates}
          preferredLocal={config.preferredLocal}
          isLocalPicker={config.isLocalPicker}
          hasAvailableSelectedDate={hasAvailableSelectedDate}
          showHighlights={
            config.isLocalPicker ||
            (config.isOriginals && config.nrOfLocals > 1)
          }
          isLoading={loading.availableDates}
        />
        <BookBlockTimeSelector
          ref={timeSelectRef}
          selectedDate={values.selectedDate}
          selectedTime={values.selectedTime}
          onTimeChange={onTimeChange}
          availableTimes={values.availableTimes}
          preferredLocal={config.preferredLocal}
          isLocalPicker={config.isLocalPicker}
          hasAvailableSelectedTime={hasAvailableSelectedTime}
          isLoading={loading.availableTimes}
        />
        <BookBlockPeopleSelector
          nrOfAdults={values.nrOfAdults}
          nrOfChildren={values.nrOfChildren}
          maxPeople={config.maxPeople}
          formattedPrice={values.formattedPrice}
          onContactHost={actions.onContactHost}
          onAdultsChange={actions.onAdultsChange}
          onChildrenChange={actions.onChildrenChange}
        />
      </div>
      {config.isLocalPicker && (
        <BookBlockSelectedLocal
          selectedLocal={config.preferredLocal}
          hasAvailableSelectedDate={hasAvailableSelectedDate}
          hasAvailableSelectedTime={hasAvailableSelectedTime}
        />
      )}
      {!config.isLocalPicker && <BookBlockLocals locals={details.locals} />}
      <div className={CSS.BookBlockButton}>
        <BookBlockButton onClick={onBookButton}>
          {config.buttonText}
        </BookBlockButton>
      </div>
    </section>
  );
}

export default BookBlock;
